/**
 * UniqueCard — carte blanche à bordure or, ombre dorée, rayon 16px.
 *
 * Fournit des sous-composants composables : Header, Title, Body, Footer.
 * Variante "glow" pour un fond or très pâle (briefings, alertes positives).
 * Variante "interactive" pour rendre la carte cliquable (rôle bouton).
 */
import type { HTMLAttributes, ReactNode } from 'react';

export type UniqueCardVariant = 'default' | 'glow' | 'flat';

export interface UniqueCardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: UniqueCardVariant;
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const paddingMap = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-8',
};

const variantMap: Record<UniqueCardVariant, string> = {
  default: 'unique-card',
  glow: 'unique-card bg-[color:var(--color-primary-glow)]',
  flat:
    'bg-white border border-[color:var(--color-border)] rounded-card ' +
    'transition-unique',
};

export function UniqueCard({
  variant = 'default',
  interactive = false,
  padding = 'md',
  className = '',
  children,
  onClick,
  ...rest
}: UniqueCardProps) {
  const composed = [
    variantMap[variant],
    paddingMap[padding],
    interactive ? 'cursor-pointer hover:-translate-y-0.5' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={composed}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if ((e.key === 'Enter' || e.key === ' ') && onClick) {
                e.preventDefault();
                onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
              }
            }
          : undefined
      }
      {...rest}
    >
      {children}
    </div>
  );
}

export function UniqueCardHeader({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-[color:var(--color-border)]">
      {children}
    </div>
  );
}

export function UniqueCardTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-base font-bold text-[color:var(--color-text-dark)] m-0">
      {children}
    </h3>
  );
}

export function UniqueCardBody({ children }: { children: ReactNode }) {
  return <div className="text-sm text-[color:var(--color-text-dark)]">{children}</div>;
}

export function UniqueCardFooter({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 pt-3 border-t border-[color:var(--color-border)] flex items-center justify-end gap-2">
      {children}
    </div>
  );
}
